import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Typography } from '@mui/material'
import { TreeItem } from '@mui/x-tree-view'
import {
  DigitalTwin,
  DigitalTwinPropertyObject,
  DigitalTwinSettingCreationRequest,
  useDigitalTwin,
  useDigitalTwinSettings,
} from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { useGetAllEventMutation } from 'api/events/events.api'
import { ObjectProperty, useObjectProperties } from 'api/objectProperties/objectProperties.api'
import { Setting, useSettings } from 'api/settings/settings.api'
import classNames from 'classnames'
import { getDisplayName, getFirstOccuringSetting } from 'helpers/global.helper/global.helper'
import {
  prettyPrintCurrentSettingValue,
  settingInPeriod,
} from 'helpers/optimizeSettings.helper/optimizeSettings.helper'
import { getDeviationSettingPriorities } from 'helpers/settingPriority.helper/settingPriority.helper'
import { useFollowupPeriod } from 'hooks/utilsHooks/utilsHooks'
import i18n from 'i18next'
import moment from 'moment'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import TreeDropdown from 'ui/molecules/TreeDropdown/TreeDropdown'
import Datetime from 'utils/datetime/datetime'
import { getPropertiesFromDigitalTwinModel } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import { getPriorityLevelDisplayNames } from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'
import { getPrettyName, getPropertyPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import EventPostViewModal from 'views/EventsView/components/EventPostViewModal/EventPostViewModal'
import { EventPost } from 'views/EventsView/components/events.helper'
import { getPeriodString, getSettingValue, getUnit, splitSettingsByTime } from './DeviationSettings.helper'
import styles from './DeviationSettings.module.less'

const deviationCategory = `deviation`

type TreeItemType = {
  setting: DigitalTwinSetting
  targetObjectName: string
  propertyName: string
  value: string
  periodString: string
  priorityString: string
  operationalEvent?: EventPost
}
function SettingTreeItem({
  // a local functional component to render setting tree items for DT, as you want to open the Event modal
  setting,
  targetObjectName,
  propertyName,
  value,
  periodString,
  priorityString,
  operationalEvent,
  itemId,
}: TreeItemType & { itemId: string }): ReactElement {
  const [openEventPost, setOpenEventPost] = useState(false)
  const { t } = useTranslation()
  return (
    <TreeItem
      key={itemId}
      itemId={itemId}
      label={
        <span className={styles.ExplorerMenu_SearchIcon}>
          <b>{`${targetObjectName}`}</b> {`${propertyName}: ${value}`}
        </span>
      }
    >
      <TreeItem
        itemId={`DeviationType_${itemId}`}
        label={
          <span>
            <b>{`${t('Deviation type')}`}</b>
            {`: ${priorityString}`}
          </span>
        }
      />
      <TreeItem
        itemId={`Period_${itemId}`}
        label={
          <span>
            <b>{`${t('Period')}`}</b>
            {`: ${periodString}`}
          </span>
        }
      />
      <TreeItem
        itemId={`Cause_${itemId}`}
        label={
          <span>
            <b>{`${t('Cause')}`}</b>
            {`: ${setting.comment}`}
          </span>
        }
      />
      {operationalEvent && (
        <>
          <TreeItem
            itemId={`OperationalEvent_${itemId}`}
            label={
              <>
                <Icon
                  icon="fal fa-calendar-week"
                  size="small"
                  data-testid="event-link-icon" // e2e: event-linked marker icon — do not remove
                  tooltip={{ title: t('View event'), arrow: true, placement: 'right' }}
                  onClick={() => setOpenEventPost(true)}
                />
                <b>{`${t('Event')}`}</b>
                {`: ${operationalEvent?.name}`}
              </>
            }
          />
          {openEventPost && (
            <EventPostViewModal closeModal={() => setOpenEventPost(false)} selectedEventPostId={operationalEvent?.id} />
          )}
        </>
      )}
    </TreeItem>
  )
}

type DeviationSettingsProps = {
  startTime: ISODateTime
  endTime: ISODateTime
  activeSettingId?: number
  specificUnitAvailability?: string
  activeUnit?: string
  specificDigitalTwin?: DigitalTwin // Used instead of the primary digital twin (in sandbox module for example).
  followUp?: boolean
  shrink?: boolean
  settingsStringSuffixType?: 'FollowupProduction' | 'DigitalTwinSettingForm' | 'OptimizationFailedGuide'
  showPreviousAndUpcomingSettings?: boolean
  newSetting?: DigitalTwinSettingCreationRequest
}

function getSettingsWithTags(settings: DigitalTwinSetting[], digitalTwin?: DigitalTwin): DigitalTwinSetting[] {
  // Get all property objects from the digital twin model
  let propertyObjects: DigitalTwinPropertyObject[] = []
  if (digitalTwin?.model) {
    propertyObjects = getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model)
  }

  // Map settings to property objects and set tags
  const settingsWithTags = settings.map((setting) => {
    const match = propertyObjects.find(
      (prop) => prop.attribute === setting.attribute && prop.name === setting.name && prop.level === setting.level
    )
    // Attach tags from the property object if found
    return match ? { ...setting, tags: match.tags } : setting
  })
  return settingsWithTags
}

function getSelectedGroupTag(
  settingsWithTags: DigitalTwinSetting[],
  activeUnit?: string,
  digitalTwin?: DigitalTwin,
  newSetting?: DigitalTwinSettingCreationRequest
): string | undefined {
  if (newSetting && digitalTwin?.model) {
    const match = getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model).find(
      (prop) =>
        prop.attribute === newSetting.attribute && prop.name === newSetting.name && prop.level === newSetting.level
    )
    return match?.tags?.find((tag) => tag.startsWith('group/'))
  }
  if (activeUnit) {
    return settingsWithTags.find((s) => s.name === activeUnit)?.tags?.find((tag) => tag.startsWith('group/'))
  }
  return undefined
}

//For OPT MODEL
const generateSettingsTreeItems = (
  settings: Setting[],
  objectProperties: ObjectProperty[],
  followUpEndTime: ISODateTime,
  startTime: ISODateTime,
  endTime: ISODateTime
): (React.JSX.Element | null)[] | undefined => {
  return objectProperties
    .map((prop) => ({ prop, setting: getFirstOccuringSetting(settings, prop, followUpEndTime) }))
    .filter(({ setting }) => !!setting)
    .map(({ prop, setting }, index) => {
      // This should never happen because of the filter above,
      // but typescript doesn't care about the filter...
      if (!setting) {
        return null
      }
      const propertyName = getDisplayName(prop)
      const value = prettyPrintCurrentSettingValue({
        startTime: Datetime.toISOString(moment(setting.start_time)),
        endTime: setting.end_time ? Datetime.toISOString(moment(setting.end_time)) : null,
        activeValue: prop.current_active_value,
        defaultValue: prop.default_value,
        periodStartTime: startTime,
        periodEndTime: endTime,
        unit: getUnit(prop.measurement_unit),
        attribute: prop.name,
        baseValue: prop.current_base_value,
      })
      const periodString = getPeriodString(setting.start_time, setting.end_time)
      return (
        <TreeItem
          key={`optmodel_${setting.id}_${prop.id}_${index}`}
          itemId={`DigitalTwinSettingsTreeItem_${index}`}
          label={
            <span className={styles.ExplorerMenu_SearchIcon}>
              <b>{`${prop.parent ? getDisplayName(prop.parent) : '-'}`}</b> {`${propertyName}: ${value}`}
            </span>
          }
        >
          <TreeItem itemId={`Period_${index}`} label={`${i18n.t('Period')}: ${periodString}`} />
          <TreeItem itemId={`Cause_${index}`} label={`${i18n.t('Cause')}: ${setting.comment}`} />
        </TreeItem>
      )
    })
}

//For Digital Twin, filters out and maps to correct SettingTreeItems
const generateDigitalTwinSettingsTreeItems = (
  settings: DigitalTwinSetting[],
  language: string | null,
  operationalEvents: Map<number, EventPost>,
  specificUnitAvailability?: string,
  activeUnit?: string,
  activeSettingId?: number,
  digitalTwin?: DigitalTwin,
  sectionKey = '',
  newSetting?: DigitalTwinSettingCreationRequest,
  activeSystem?: System | null
): (React.JSX.Element | null)[] | undefined => {
  const settingsWithTags = getSettingsWithTags(settings, digitalTwin)
  const selectedGroupTag = getSelectedGroupTag(settingsWithTags, activeUnit, digitalTwin, newSetting)

  return settingsWithTags
    .filter((setting) => setting.id != activeSettingId)
    .filter((setting) => {
      if (specificUnitAvailability) {
        return setting.name === specificUnitAvailability
      } else if (selectedGroupTag) {
        return Array.isArray(setting.tags) && setting.tags.includes(selectedGroupTag)
      } else if (activeUnit) {
        return setting.name === activeUnit
      }
      return true
    })
    .sort((a, b) => {
      if (a.name && b.name) {
        const nameA = getPrettyName(a.name, digitalTwin?.translations)
        const nameB = getPrettyName(b.name, digitalTwin?.translations)
        return nameA.localeCompare(nameB, language ?? 'sv')
      }
      return 0
    })
    .map((setting) => {
      const targetObjectName = getPrettyName(setting.name, digitalTwin?.translations)
      const propertyName = getPropertyPrettyName(setting.name, setting.attribute, digitalTwin?.translations)
      const value = getSettingValue(setting.value, null, null, setting.isTimeSeries, setting.attribute)
      const operationalEvent = operationalEvents.get(setting.operational_event ?? -1)
      const periodString = getPeriodString(
        setting.start_time,
        setting.end_time ? Datetime.ISODatetimeTo59Minutes(setting.end_time) : setting.end_time
      )
      const priorityString = getPriorityLevelDisplayNames(setting.priority, activeSystem?.setting_priority_levels)
      const itemId = `DigitalTwinSettingsTreeItem_${sectionKey}_${setting.id}`
      return (
        <SettingTreeItem
          key={itemId}
          setting={setting}
          periodString={periodString}
          priorityString={priorityString}
          value={value}
          targetObjectName={targetObjectName}
          propertyName={propertyName}
          operationalEvent={operationalEvent}
          itemId={itemId}
        />
      )
    })
}

export default function DeviationSettings({
  startTime,
  endTime,
  activeSettingId,
  specificUnitAvailability,
  activeUnit,
  specificDigitalTwin,
  followUp,
  shrink,
  settingsStringSuffixType,
  showPreviousAndUpcomingSettings,
  newSetting,
}: DeviationSettingsProps): ReactElement {
  const [digitalTwinSettings, setDigitalTwinSettings] = useState<DigitalTwinSetting[]>([])
  const [operationalEvents, setOperationalEvents] = useState<Map<number, EventPost>>(new Map<number, EventPost>())

  const [followupPeriod] = useFollowupPeriod()
  const followUpEndTime = Datetime.toISOString(followupPeriod.endTime)
  const twelveHoursAgo = Datetime.subtractHours(startTime, 12)
  const twelveHoursAhead = Datetime.addHours(endTime, 12)

  const { t } = useTranslation()
  const language = localStorage.getItem('language')
  const { activeSystem } = useAuth()
  const systemPrimaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: primaryDigitalTwin } = useDigitalTwin(systemPrimaryDigitalTwin?.uid)

  const digitalTwin = specificDigitalTwin ?? primaryDigitalTwin

  const { data: objectProperties = [], isPending: optModelPropertiesIsLoading } = useObjectProperties({
    start_time: startTime.toString(),
    end_time: endTime.toString(),
  })
  const { data: optModelSettings, isPending: optModelSettingLoading } = useSettings({
    params: {
      active_from: startTime.toString(),
      active_to: endTime.toString(),
    },
  })

  const {
    mutateAsync: fetchDigitalTwinSettings,
    isPending: isPendingSettings,
    isIdle: isIdleSettings,
  } = useDigitalTwinSettings()
  const isLoadingDigitalTwinSettings = isPendingSettings || isIdleSettings

  useEffect(() => {
    if (digitalTwin?.uid !== undefined) {
      fetchDigitalTwinSettings({
        digitalTwinUid: digitalTwin?.uid,
        activeFrom: Datetime.toISOString(twelveHoursAgo),
        activeTo: Datetime.toISOString(twelveHoursAhead),
        priorities: getDeviationSettingPriorities(activeSystem?.setting_priority_levels),
      }).then((settings) => {
        setDigitalTwinSettings(settings)
      })
    }
  }, [activeSystem, digitalTwin?.uid, fetchDigitalTwinSettings, startTime, endTime])

  const { mutateAsync: fetchOperationalEvents } = useGetAllEventMutation()
  const fetchEvents = useCallback(() => {
    const ids = Array.from(
      new Set(
        digitalTwinSettings.map((setting) => setting.operational_event).filter((id) => id !== undefined && id !== null)
      )
    )
    if (ids.length === 0) {
      return
    }

    fetchOperationalEvents({
      ids: ids,
    }).then((data) => {
      const dict = new Map<number, EventPost>()
      data.forEach((event) => {
        if (event.id !== undefined) {
          dict.set(event.id, event)
        }
      })
      setOperationalEvents(dict)
    })
  }, [fetchOperationalEvents, digitalTwinSettings])

  const onOpenSettingsDropdown = useCallback(
    (open: boolean) => {
      if (open) {
        fetchEvents()
      }
    },
    [fetchEvents]
  )

  const filteredOptModelSettings = useMemo(() => {
    return optModelSettings?.filter((setting) => {
      const property = objectProperties.find((prop) => setting.object_property === prop.id)
      if (property && !setting.is_base_value) {
        if (followUp) {
          return property.classifications.includes(deviationCategory)
        } else {
          return (
            property.classifications.includes(deviationCategory) &&
            settingInPeriod(moment(setting.start_time), setting.end_time !== null ? moment(setting.end_time) : null)
          )
        }
      }
      return false
    })
  }, [optModelSettings, objectProperties, followUp])

  const settingsAreLoading =
    !digitalTwin && filteredOptModelSettings?.length
      ? optModelSettingLoading || optModelPropertiesIsLoading
      : isLoadingDigitalTwinSettings

  const settingsWithTags = useMemo(
    () => getSettingsWithTags(digitalTwinSettings, digitalTwin),
    [digitalTwinSettings, digitalTwin]
  )
  const selectedGroupTag = getSelectedGroupTag(settingsWithTags, activeUnit, digitalTwin, newSetting)

  let settingsStringSuffix = ''
  if (settingsStringSuffixType === 'FollowupProduction') {
    settingsStringSuffix = t('for current period')
  } else if (settingsStringSuffixType === 'DigitalTwinSettingForm') {
    settingsStringSuffix = selectedGroupTag ? t('related to current unit') : t('for current unit')
  } else if (settingsStringSuffixType === 'OptimizationFailedGuide') {
    settingsStringSuffix = t('for the optimization period')
  }

  // Split settings into previous, active, and upcoming for Digital Twin and Optmodel
  const splitDigitalTwinSettings = useMemo(
    () =>
      splitSettingsByTime<DigitalTwinSetting>(digitalTwinSettings, {
        startTime,
        endTime,
      }),
    [digitalTwinSettings, startTime, endTime]
  )
  const splitOptModelSettings = useMemo(
    () =>
      splitSettingsByTime<Setting>(filteredOptModelSettings || [], {
        startTime,
        endTime,
      }),
    [filteredOptModelSettings, startTime, endTime]
  )

  // Generate tree items for each section
  const getTreeItems = (settings: DigitalTwinSetting[], sectionKey: string) =>
    generateDigitalTwinSettingsTreeItems(
      settings,
      language,
      operationalEvents,
      specificUnitAvailability,
      activeUnit,
      activeSettingId,
      digitalTwin,
      sectionKey,
      newSetting,
      activeSystem
    )

  // For collapsible sections
  const [openActive, setOpenActive] = React.useState<boolean>(true)
  const [openPrevious, setOpenPrevious] = React.useState<boolean>(false)
  const [openUpcoming, setOpenUpcoming] = React.useState<boolean>(false)

  const getSectionSettings = (settings: DigitalTwinSetting[] | Setting[], sectionKey: string) => {
    let filtered: (React.JSX.Element | null)[] = []
    if (!digitalTwin && Array.isArray(settings) && settings.length && objectProperties.length) {
      // OPT MODEL: only show active section, no grouping
      filtered =
        generateSettingsTreeItems(settings as Setting[], objectProperties, followUpEndTime, startTime, endTime) || []
    } else {
      // DIGITAL TWIN: use existing logic
      filtered = getTreeItems(settings as DigitalTwinSetting[], sectionKey) || []
    }
    const hasSettings = filtered.length > 0 && filtered.some(Boolean)
    return { filtered, hasSettings }
  }

  const getMultipleSectionSettings = (
    sections: { settings: DigitalTwinSetting[] | Setting[]; sectionKey: string }[]
  ) => {
    return sections.map(({ settings, sectionKey }) => getSectionSettings(settings, sectionKey))
  }

  const multipleSectionSettings = getMultipleSectionSettings([
    { settings: splitDigitalTwinSettings.previous, sectionKey: 'previous' },
    { settings: splitDigitalTwinSettings.active, sectionKey: 'active' },
    { settings: splitDigitalTwinSettings.upcoming, sectionKey: 'upcoming' },
  ])

  // Helper to render a section (works for both digital twin and optmodel settings)
  const renderSection = (
    label: string,
    filtered: (React.JSX.Element | null)[],
    hasSettings: boolean,
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    showHeader = true
  ) => {
    return (
      <div>
        {showHeader && (
          <Tooltip title={!hasSettings ? t('No settings in this time period') : null} arrow placement="top">
            <div
              className={styles.DeviationSettings_TabHeader}
              style={!hasSettings ? { opacity: 0.5 } : {}}
              onClick={!hasSettings ? undefined : () => setOpen((v) => !v)}
            >
              {!hasSettings ? (
                <ChevronRightIcon fontSize="small" style={{ visibility: 'hidden' }} />
              ) : open ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
              <Icon color={'yellow'} size="medium" icon={'fas fa-info-circle'} />
              <span className={styles.DeviationSettings_TabHeaderLabel}>{label}</span>
            </div>
          </Tooltip>
        )}
        {open && filtered}
      </div>
    )
  }

  // Generate dropdown content
  let dropdownContent: (React.JSX.Element | null)[] = []
  if (showPreviousAndUpcomingSettings && digitalTwin && multipleSectionSettings.length === 3) {
    dropdownContent = [
      renderSection(
        t('Previous (12 hours back)'),
        multipleSectionSettings[0].filtered,
        multipleSectionSettings[0].hasSettings,
        openPrevious,
        setOpenPrevious,
        true
      ),
      renderSection(
        t('Active'),
        multipleSectionSettings[1].filtered,
        multipleSectionSettings[1].hasSettings,
        openActive,
        setOpenActive,
        true
      ),
      renderSection(
        t('Upcoming (12 hours ahead)'),
        multipleSectionSettings[2].filtered,
        multipleSectionSettings[2].hasSettings,
        openUpcoming,
        setOpenUpcoming,
        true
      ),
    ]
  } else if (!digitalTwin && filteredOptModelSettings?.length) {
    dropdownContent =
      generateSettingsTreeItems(splitOptModelSettings.active, objectProperties, followUpEndTime, startTime, endTime) ||
      []
  } else {
    dropdownContent = getTreeItems(splitDigitalTwinSettings.active, 'active') || []
  }

  let numberOfSettings = 0
  if (showPreviousAndUpcomingSettings && digitalTwin) {
    numberOfSettings = multipleSectionSettings.reduce(
      (acc, section) => acc + (section.filtered?.filter(Boolean).length || 0),
      0
    )
  } else {
    numberOfSettings = dropdownContent.filter(Boolean).length
  }

  const numberOfSettingsTitle =
    numberOfSettings === 1
      ? t(`{{number}} active deviation setting {{settingsSuffix}}`, {
          number: numberOfSettings,
          settingsSuffix: settingsStringSuffix,
        })
      : t(`{{number}} active deviation settings {{settingsSuffix}}`, {
          number: numberOfSettings,
          settingsSuffix: settingsStringSuffix,
        })

  const noActiveSettingsTitle = settingsStringSuffix
    ? t(`No active deviation settings found {{settingsStringSuffix}}.`, {
        settingsStringSuffix: settingsStringSuffix,
      })
    : t(`No active deviation settings found for this period.`)

  return (
    <div className={classNames(styles.DeviationSettings_Container, shrink ? styles.DeviationSettings_Shrink : '')}>
      {settingsAreLoading ? (
        <Typography variant="caption">{t(`Loading active deviation settings...`)}</Typography>
      ) : numberOfSettings !== 0 ? (
        <TreeDropdown customButtonTitle={numberOfSettingsTitle} handleToggle={onOpenSettingsDropdown}>
          {dropdownContent}
        </TreeDropdown>
      ) : (
        <div className={styles.DeviationSettings_TypographyContainer}>
          <Typography variant="body1">{noActiveSettingsTitle}</Typography>
        </div>
      )}
    </div>
  )
}
